// src/routes/index.tsx

import React, { Suspense } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { MainLayout } from '@/components/layout/MainLayout';
import { LoadingSpinner } from '@guardian/shared-ui';
import { FormValidationGuard } from '@/components/guards/FormValidationGuard';
import { ErrorFallback } from '@/components/ErrorFallback';

// Retry wrapper for dynamic imports.
// On final failure (stale chunks after deploy), reloads the page once
// so the browser fetches the new index.html with updated chunk references.
const RELOAD_KEY = 'chunk_reload';

function lazyWithRetry<T extends React.ComponentType>(
  importFn: () => Promise<{ default: T }>,
  retries = 2,
  delay = 1000
): React.LazyExoticComponent<T> {
  return React.lazy(() => {
    const tryImport = async (attempt: number): Promise<{ default: T }> => {
      try {
        return await importFn();
      } catch (error) {
        if (attempt < retries) {
          await new Promise(resolve => setTimeout(resolve, delay));
          return tryImport(attempt + 1);
        }
        // After retries exhausted, reload once to get fresh chunks
        if (!sessionStorage.getItem(RELOAD_KEY)) {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
        }
        throw error;
      }
    };
    // Clear the reload flag on successful load
    sessionStorage.removeItem(RELOAD_KEY);
    return tryImport(0);
  });
}

// Lazy load components with retry for Safari compatibility
const HomePage = lazyWithRetry(() => import('@/pages/home-page'));
const FormPage = lazyWithRetry(() => import('@/pages/form/FormPage'));
const GuestFormPage = lazyWithRetry(() => import('@/pages/form/GuestFormPage'));
const VehicleFormPage = lazyWithRetry(() => import('@/pages/form/VehicleFormPage'));
const PetFormPage = lazyWithRetry(() => import('@/pages/form/PetFormPage'));
const GuestCheckinPage = lazyWithRetry(() => import('@/pages/guest-checkin/GuestCheckinPage'));
const ConnectionErrorPage = lazyWithRetry(() => import('@/pages/ConnectionErrorPage'));

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense
    fallback={
      <div className="flex h-[calc(100dvh-4rem)] w-full items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    }
  >
    {children}
  </Suspense>
);

// Module-scope export so tests (forms-plural-alias.test.tsx) can drive useRoutes(routes).
// Plural /forms/* entries are the new canonical (Decision 31c / PRD form-app-white-labeling.md:43);
// singular /form/* entries are kept as back-compat aliases for the FR-DOM-2 transition window.
export const routes = [
  {
    path: '/',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <HomePage />
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/checkin/:listingPublicId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <GuestCheckinPage />
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <FormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/guest/:guestId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <GuestFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/vehicle/:vehicleId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <VehicleFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/forms/:formId/pet/:petId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <PetFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/form/:formId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <FormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/form/:formId/guest/:guestId',
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <GuestFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '/connection-error',
    element: (
      <SuspenseWrapper>
        <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
          <ConnectionErrorPage />
        </Sentry.ErrorBoundary>
      </SuspenseWrapper>
    ),
  },
  {
    path: "/form/:formId/vehicle/:vehicleId",
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <VehicleFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: "/form/:formId/pet/:petId",
    element: (
      <MainLayout>
        <SuspenseWrapper>
          <Sentry.ErrorBoundary fallback={<ErrorFallback />} showDialog={false}>
            <FormValidationGuard>
              <PetFormPage />
            </FormValidationGuard>
          </Sentry.ErrorBoundary>
        </SuspenseWrapper>
      </MainLayout>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

export const AppRoutes = () => useRoutes(routes);
